import React, { useState, useEffect } from 'react';
import { Input, AutoComplete, Checkbox, Button, Avatar, Spin, Typography, App } from 'antd';
import { PhoneOutlined, UserOutlined, HomeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import {
  useLazySearchCustomerByPhoneQuery,
  useLazySearchCustomersQuery,
  useAddCustomerAddressMutation,
} from '../../../services/customerApi';
import { Customer, CustomerAddress, CreateCustomerAddressDto } from '../../../types/customer';
import { formatAddress } from '../../../utils/formatAddress';

const { Text } = Typography;

const ADDRESS_LABELS = ['Casa', 'Oficina', 'Otro'];

interface DeliveryCustomerStripProps {
  selectedCustomer: Customer | null;
  onSelectCustomer: (customer: Customer | null) => void;
  name: string;
  onNameChange: (value: string) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  selectedAddress: CustomerAddress | null;
  onSelectAddress: (address: CustomerAddress | null) => void;
  saveCustomer: boolean;
  onSaveCustomerChange: (value: boolean) => void;
  deliveryNotes: string;
  onDeliveryNotesChange: (value: string) => void;
  /** Edit mode: customer identity is fixed */
  disabled?: boolean;
}

/**
 * Delivery customer strip: phone-first lookup. A found customer becomes a chip
 * with their addresses as selectable pills; an unknown phone reveals the
 * new-customer fields inline. "+ Nueva dirección" expands an attached row —
 * never a modal, so the menu stays available while the customer dictates.
 */
const DeliveryCustomerStrip: React.FC<DeliveryCustomerStripProps> = ({
  selectedCustomer,
  onSelectCustomer,
  name,
  onNameChange,
  phone,
  onPhoneChange,
  selectedAddress,
  onSelectAddress,
  saveCustomer,
  onSaveCustomerChange,
  deliveryNotes,
  onDeliveryNotesChange,
  disabled = false,
}) => {
  const { message } = App.useApp();
  const [searchCustomer, { data: searchResults = [], isFetching: isSearching }] =
    useLazySearchCustomerByPhoneQuery();
  const [searchCustomers, { data: nameSearchResults = [], isFetching: isSearchingByName }] =
    useLazySearchCustomersQuery();
  const [addCustomerAddress] = useAddCustomerAddressMutation();

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addrLabel, setAddrLabel] = useState('Casa');
  const [addrStreet, setAddrStreet] = useState('');
  const [addrExteriorNumber, setAddrExteriorNumber] = useState('');
  const [addrColony, setAddrColony] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrPostalCode, setAddrPostalCode] = useState('');
  const [addrReference, setAddrReference] = useState('');

  const resetAddressForm = () => {
    setAddrLabel('Casa');
    setAddrStreet('');
    setAddrExteriorNumber('');
    setAddrColony('');
    setAddrCity('');
    setAddrPostalCode('');
    setAddrReference('');
  };

  // Debounced phone lookup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!disabled && !selectedCustomer && phone.length >= 3) {
        searchCustomer(phone);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [phone, disabled, selectedCustomer, searchCustomer]);

  // Debounced name lookup — regulars are found by either field
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!disabled && !selectedCustomer && name.length >= 2) {
        searchCustomers(name);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [name, disabled, selectedCustomer, searchCustomers]);

  const isNewCustomer = !selectedCustomer && phone.length >= 7 && !isSearching && searchResults.length === 0;

  const handleSelectCustomer = (customer: Customer) => {
    onSelectCustomer(customer);
    onNameChange(customer.name);
    onPhoneChange(customer.phone);
    if (customer.addresses?.length) {
      const defaultAddr = customer.addresses.find(a => a.isDefault) || customer.addresses[0];
      onSelectAddress(defaultAddr);
      setShowAddressForm(false);
    } else {
      onSelectAddress(null);
      setShowAddressForm(true);
    }
  };

  const handleClearCustomer = () => {
    onSelectCustomer(null);
    onSelectAddress(null);
    onNameChange('');
    onPhoneChange('');
    setShowAddressForm(false);
  };

  const handleSaveAddress = async () => {
    if (!addrStreet) {
      message.warning('La calle es requerida');
      return;
    }
    const addressData = {
      label: addrLabel,
      street: addrStreet,
      exteriorNumber: addrExteriorNumber || undefined,
      interiorNumber: undefined,
      colony: addrColony || undefined,
      city: addrCity || undefined,
      postalCode: addrPostalCode || undefined,
      reference: addrReference || undefined,
    };

    try {
      if (selectedCustomer) {
        const newAddress = await addCustomerAddress({
          customerId: selectedCustomer.id,
          data: addressData as CreateCustomerAddressDto,
        }).unwrap();
        onSelectCustomer({
          ...selectedCustomer,
          addresses: [...(selectedCustomer.addresses || []), newAddress],
        });
        onSelectAddress(newAddress);
      } else {
        // Temp address (empty id): persisted together with the customer at order creation
        onSelectAddress({
          id: '',
          customerId: '',
          restaurantId: '',
          label: addrLabel,
          street: addrStreet,
          exteriorNumber: addrExteriorNumber,
          interiorNumber: '',
          colony: addrColony,
          city: addrCity,
          postalCode: addrPostalCode,
          reference: addrReference,
          isActive: true,
          isDefault: false,
          createdAt: new Date().toISOString(),
          fullAddress: formatAddress(addressData),
        });
      }
      setShowAddressForm(false);
      resetAddressForm();
    } catch (error) {
      console.error('Error adding address:', error);
      message.error('Error al guardar la dirección');
    }
  };

  const addresses = selectedCustomer?.addresses || [];
  // Temp address (no id) shows as a pill too
  const showTempAddressPill = !selectedCustomer && selectedAddress && !selectedAddress.id;

  return (
    <div className="ot-customer ot-customer--expanded">
      {/* Row 1: identity */}
      <div className="ot-customer__row">
        {selectedCustomer ? (
          <>
            <span className="ot-customer__chip">
              <Avatar size={24} style={{ backgroundColor: 'var(--color-primary)', fontSize: 11, fontWeight: 700 }}>
                {selectedCustomer.name?.charAt(0).toUpperCase()}
              </Avatar>
              <span>
                <b>{selectedCustomer.name}</b>{' '}
                <small>
                  · {selectedCustomer.phone}
                  {selectedCustomer.orderCount > 0 ? ` · ${selectedCustomer.orderCount} órdenes` : ''}
                </small>
              </span>
            </span>
            {!disabled && (
              <Button size="small" onClick={handleClearCustomer}>Cambiar cliente</Button>
            )}
          </>
        ) : (
          <>
            <AutoComplete
              value={phone}
              onChange={(value) => onPhoneChange(value)}
              options={searchResults.map((c) => ({
                value: c.phone,
                label: (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span><Text strong>{c.name}</Text> <Text type="secondary">{c.phone}</Text></span>
                    {c.orderCount > 0 && <Text type="secondary">{c.orderCount} órdenes</Text>}
                  </div>
                ),
                customer: c,
              }))}
              onSelect={(_, option: any) => handleSelectCustomer(option.customer)}
              className="ot-field ot-field--phone"
              disabled={disabled}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Teléfono *"
                suffix={isSearching ? <Spin size="small" /> : null}
              />
            </AutoComplete>
            {isNewCustomer && (
              <span className="ot-customer__hint">Sin resultados — cliente nuevo</span>
            )}
            <AutoComplete
              value={name}
              onChange={(value) => onNameChange(value)}
              options={nameSearchResults.map((c) => ({
                value: c.name,
                label: (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span><Text strong>{c.name}</Text> <Text type="secondary">{c.phone}</Text></span>
                    {c.orderCount > 0 && <Text type="secondary">{c.orderCount} órdenes</Text>}
                  </div>
                ),
                customer: c,
              }))}
              onSelect={(_, option: any) => handleSelectCustomer(option.customer)}
              className="ot-field ot-field--name"
              disabled={disabled}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Nombre del cliente *"
                suffix={isSearchingByName ? <Spin size="small" /> : null}
              />
            </AutoComplete>
            {!disabled && (
              <Checkbox
                checked={saveCustomer}
                onChange={(e) => onSaveCustomerChange(e.target.checked)}
              >
                Guardar para futuras órdenes
              </Checkbox>
            )}
          </>
        )}
      </div>

      {/* Row 2: addresses + delivery notes */}
      {!disabled && (
        <div className="ot-customer__row">
          {addresses.map((addr) => (
            <button
              key={addr.id}
              type="button"
              className={`ot-addr${selectedAddress?.id === addr.id ? ' ot-addr--selected' : ''}`}
              onClick={() => { onSelectAddress(addr); setShowAddressForm(false); }}
            >
              <HomeOutlined />
              <span>{addr.label} · {addr.fullAddress}</span>
            </button>
          ))}
          {showTempAddressPill && (
            <button type="button" className="ot-addr ot-addr--selected">
              <HomeOutlined />
              <span>{selectedAddress!.label} · {selectedAddress!.fullAddress}</span>
            </button>
          )}
          <button
            type="button"
            className="ot-addr"
            onClick={() => setShowAddressForm(v => !v)}
          >
            <EnvironmentOutlined /> {showAddressForm ? 'Cancelar' : '+ Nueva dirección'}
          </button>

          <Input
            className="ot-field ot-field--notes"
            placeholder="Notas de entrega (opcional)"
            value={deliveryNotes}
            onChange={(e) => onDeliveryNotesChange(e.target.value)}
          />
        </div>
      )}

      {/* Row 3: inline new-address form — attached, never a modal */}
      {showAddressForm && !disabled && (
        <div className="ot-addr-form">
          <span className="ot-label-pills" style={{ display: 'inline-flex', gap: 4 }}>
            {ADDRESS_LABELS.map((label) => (
              <button
                key={label}
                type="button"
                className={`ot-label-pill${addrLabel === label ? ' ot-label-pill--selected' : ''}`}
                onClick={() => setAddrLabel(label)}
              >
                {label}
              </button>
            ))}
          </span>
          <Input placeholder="Calle *" value={addrStreet} onChange={(e) => setAddrStreet(e.target.value)} style={{ flex: '1 1 160px' }} />
          <Input placeholder="Número" value={addrExteriorNumber} onChange={(e) => setAddrExteriorNumber(e.target.value)} style={{ flex: '0 1 90px' }} />
          <Input placeholder="Colonia" value={addrColony} onChange={(e) => setAddrColony(e.target.value)} style={{ flex: '0 1 140px' }} />
          <Input placeholder="Ciudad" value={addrCity} onChange={(e) => setAddrCity(e.target.value)} style={{ flex: '0 1 120px' }} />
          <Input placeholder="C.P." value={addrPostalCode} onChange={(e) => setAddrPostalCode(e.target.value)} style={{ flex: '0 1 80px' }} />
          <Input placeholder="Referencias (portón negro, entre calles…)" value={addrReference} onChange={(e) => setAddrReference(e.target.value)} style={{ flex: '1 1 200px' }} />
          <Button type="primary" size="small" onClick={handleSaveAddress}>Guardar dirección</Button>
        </div>
      )}
    </div>
  );
};

export default DeliveryCustomerStrip;
